import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Language as LanguageIcon,
  CheckCircle as CheckIcon,
  Storefront as StorefrontIcon,
  SmartToy as SmartToyIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

import PasswordField from '../components/forms/PasswordField';

/* ─── Brand tokens ─── */
const BG = '#0a0a0a';
const BG_CARD = '#111111';
const BG_SURFACE = '#161616';
const ELECTRIC_BLUE = '#2196f3';
const ORANGE = '#ff9800';
const GREEN = '#00e676';
const TEXT = '#e5e5e5';
const TEXT_DIM = '#888888';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: { default: BG, paper: BG_CARD },
    primary: { main: ELECTRIC_BLUE },
    text: { primary: TEXT, secondary: TEXT_DIM },
  },
  typography: {
    fontFamily:
      '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif',
  },
  shape: { borderRadius: 12 },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: 'none', fontWeight: 600, borderRadius: 8 },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
      },
    },
  },
});

/* ─── Dark styled TextField ─── */
const darkTextFieldSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: BG_SURFACE,
    color: TEXT,
    '& fieldset': { borderColor: '#333' },
    '&:hover fieldset': { borderColor: '#555' },
    '&.Mui-focused fieldset': { borderColor: ELECTRIC_BLUE },
  },
  '& .MuiInputLabel-root': { color: TEXT_DIM },
  '& .MuiInputLabel-root.Mui-focused': { color: ELECTRIC_BLUE },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: TEXT_DIM },
  '& .MuiFormHelperText-root': { color: TEXT_DIM },
};

/* ═══════════════════════════════════════════════════════
   FORM TYPES
   ═══════════════════════════════════════════════════════ */
interface RegistrationForm {
  companyName: string;
  contactName: string;
  websiteUrl: string;
  email: string;
  password: string;
  confirmPassword: string;
  referralCode?: string;
  termsAccepted: boolean;
}

/* ═══════════════════════════════════════════════════════
   MAIN PAGE — Streamlined registration
   ═══════════════════════════════════════════════════════ */
const RegisterPartner: React.FC = () => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Extract referral code from URL param or sessionStorage
  useEffect(() => {
    const { ref } = router.query;
    if (ref && typeof ref === 'string') {
      setForm((prev) => ({ ...prev, referralCode: ref }));
    } else {
      try {
        const storedRef = sessionStorage.getItem('aithon_ref');
        if (storedRef) {
          setForm((prev) => ({ ...prev, referralCode: storedRef }));
        }
      } catch {}
    }
  }, [router.query]);

  const [form, setForm] = useState<RegistrationForm>({
    companyName: '',
    contactName: '',
    websiteUrl: '',
    email: '',
    password: '',
    confirmPassword: '',
    referralCode: '',
    termsAccepted: false,
  });

  const handleInputChange = (
    field: keyof RegistrationForm,
    value: string | boolean,
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (
        !form.companyName ||
        !form.contactName ||
        !form.email ||
        !form.password
      ) {
        throw new Error('Please fill in all required fields');
      }

      if (form.password.length < 8) {
        throw new Error('Password must be at least 8 characters long');
      }

      if (form.password !== form.confirmPassword) {
        throw new Error('Passwords do not match');
      }

      const requestData: any = {
        type: 'partner',
        companyName: form.companyName,
        contactName: form.contactName,
        email: form.email,
        password: form.password,
        websiteUrl: form.websiteUrl || undefined,
        termsAccepted: form.termsAccepted,
      };

      if (form.referralCode?.trim()) requestData.referralCode = form.referralCode.trim();

      const response = await fetch(`/api/partners/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json();

        if (response.status === 409) {
          if (
            errorData.error === 'Subdomain unavailable' ||
            errorData.error === 'Subdomain generation failed'
          ) {
            throw new Error(`${errorData.message} ${errorData.suggestion || ''}`);
          } else if (errorData.error === 'Email already registered') {
            throw new Error(`${errorData.message} ${errorData.suggestion || ''}`);
          } else {
            throw new Error(
              errorData.message || 'This information conflicts with an existing account.',
            );
          }
        } else if (response.status === 503) {
          throw new Error('Service temporarily unavailable. Please try again in a few minutes.');
        } else {
          throw new Error(errorData.message || 'Registration failed. Please try again.');
        }
      }

      const result = await response.json();

      sessionStorage.setItem(
        'partnerRegistrationCredentials',
        JSON.stringify({
          email: form.email,
          password: form.password,
        }),
      );

      try {
        if (form.websiteUrl?.trim()) {
          sessionStorage.setItem('partnerWebsiteUrl', form.websiteUrl.trim());
        }
        if (form.companyName?.trim()) {
          sessionStorage.setItem('partnerCompanyName', form.companyName.trim());
        }
      } catch {}

      if (result.requiresPayment) {
        const amount = result.amount || 5000;
        const authToken = result.token || result.accessToken || '';
        router.push(
          `/partner/payment?partnerId=${result.partnerId}&subscriptionId=${result.subscriptionId}&amount=${amount}&pending=true${form.websiteUrl?.trim() ? `&websiteUrl=${encodeURIComponent(form.websiteUrl.trim())}` : ''}${authToken ? `&authToken=${encodeURIComponent(authToken)}` : ''}`,
        );
      } else {
        router.push(`/partners/onboarding${form.websiteUrl?.trim() ? `?websiteUrl=${encodeURIComponent(form.websiteUrl.trim())}` : ''}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <Head>
        <title>Create Your Storefront | Aithon</title>
        <meta
          name="description"
          content="Launch your branded storefront on Aithon. $50 one-time setup deposit, refunded on your first sale, plus a 5% success fee only when deals close."
        />
        <link rel="canonical" href="https://aithon.tech/registerpartner" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Create Your Storefront | Aithon" />
        <meta property="og:description" content="Launch your branded storefront on Aithon. $50 one-time setup deposit, refunded on your first sale, plus a 5% success fee only when deals close." />
        <meta property="og:url" content="https://aithon.tech/registerpartner" />
        <meta property="og:image" content="https://aithon.tech/og-default.png" />
        <meta property="og:site_name" content="Aithon" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Create Your Storefront | Aithon" />
        <meta name="twitter:description" content="Launch your branded storefront on Aithon. $50 one-time setup deposit, refunded on your first sale, plus a 5% success fee only when deals close." />
        <meta name="twitter:image" content="https://aithon.tech/og-default.png" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <link rel="alternate" type="application/json" href="https://aithon.tech/.well-known/agents.json" />
      </Head>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{__html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "url": "https://aithon.tech/registerpartner",
          "name": "Create Your Storefront | Aithon",
          "description": "Launch your branded storefront on Aithon. $50 one-time setup deposit, refunded on your first sale, plus a 5% success fee only when deals close."
        })}}
      />

      <Box sx={{ bgcolor: BG, minHeight: '100vh', color: TEXT }}>
        <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
          {/* Back to About */}
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => router.push('/about')}
            sx={{ color: TEXT_DIM, mb: 3, '&:hover': { color: ORANGE } }}
          >
            About Aithon
          </Button>

          {/* Value prop bullets */}
          <Box sx={{ mb: 4 }}>
            <Stack spacing={1.5}>
              {[
                { icon: <StorefrontIcon sx={{ fontSize: 20 }} />, text: 'Your own branded storefront — live in minutes' },
                { icon: <SmartToyIcon sx={{ fontSize: 20 }} />, text: 'AI discovers your services and brings you buyers' },
                { icon: <TrendingUpIcon sx={{ fontSize: 20 }} />, text: 'Pay per closed sale — 5% success fee only when contracts complete' },
                { icon: <CheckIcon sx={{ fontSize: 20 }} />, text: 'Upgrade to Verified Organization for 3%' },
              ].map((item, i) => (
                <Stack key={i} direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ color: GREEN, flexShrink: 0 }}>{item.icon}</Box>
                  <Typography sx={{ color: TEXT, fontSize: '1rem', lineHeight: 1.5 }}>
                    {item.text}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Card
            sx={{
              bgcolor: BG_CARD,
              border: '1px solid #222',
              borderRadius: 3,
              position: 'relative',
              overflow: 'visible',
            }}
          >
            <CardContent sx={{ p: { xs: 3, md: 5 } }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    mb: 1,
                    background: `linear-gradient(135deg, #fff 0%, #ccc 100%)`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Create Your Storefront
                </Typography>
                <Typography sx={{ color: TEXT_DIM, lineHeight: 1.6 }}>
                  $50 one-time setup deposit — refunded on your first sale. After that, it’s a 5% success fee only when deals close.
                </Typography>
              </Box>

              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    bgcolor: 'rgba(244, 67, 54, 0.08)',
                    border: '1px solid rgba(244, 67, 54, 0.3)',
                    color: TEXT,
                  }}
                >
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Company Name *"
                      value={form.companyName}
                      onChange={(e) => handleInputChange('companyName', e.target.value)}
                      placeholder="Your Company LLC"
                      required
                      sx={darkTextFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Your Name *"
                      value={form.contactName}
                      onChange={(e) => handleInputChange('contactName', e.target.value)}
                      placeholder="Jane Smith"
                      required
                      sx={darkTextFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Website URL"
                      value={form.websiteUrl}
                      onChange={(e) => handleInputChange('websiteUrl', e.target.value)}
                      placeholder="https://yourcompany.com"
                      helperText="Our AI scans your site to suggest services — saves you setup time"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LanguageIcon />
                          </InputAdornment>
                        ),
                      }}
                      sx={darkTextFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email *"
                      type="email"
                      value={form.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="you@company.com"
                      autoComplete="username"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon />
                          </InputAdornment>
                        ),
                      }}
                      required
                      sx={darkTextFieldSx}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PasswordField
                      label="Password *"
                      value={form.password}
                      onChange={(value) => handleInputChange('password', value)}
                      error={!!error && !form.password}
                      helperText={
                        error && !form.password
                          ? 'Password is required'
                          : 'Create a secure password'
                      }
                      showStrengthIndicator={true}
                      required
                      inputProps={{ 'data-testid': 'password' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <PasswordField
                      label="Confirm Password *"
                      value={form.confirmPassword}
                      onChange={(value) => handleInputChange('confirmPassword', value)}
                      error={!!(form.confirmPassword && form.password !== form.confirmPassword)}
                      helperText={
                        form.confirmPassword && form.password !== form.confirmPassword
                          ? 'Passwords do not match'
                          : 'Confirm your password'
                      }
                      showStrengthIndicator={false}
                      required
                      inputProps={{ 'data-testid': 'confirmPassword' }}
                    />
                  </Grid>
                </Grid>

                {/* Terms */}
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.termsAccepted}
                        onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
                        sx={{
                          color: TEXT_DIM,
                          '&.Mui-checked': { color: ELECTRIC_BLUE },
                        }}
                        required
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: TEXT_DIM }}>
                        I agree to the{' '}
                        <a
                          href="/terms"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: ELECTRIC_BLUE, textDecoration: 'underline' }}
                        >
                          Terms and Conditions
                        </a>
                      </Typography>
                    }
                  />
                </Box>

                {/* Submit */}
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isLoading || !form.termsAccepted}
                  sx={{
                    mt: 3,
                    py: 2,
                    fontSize: '1.1rem',
                    borderRadius: 50,
                    bgcolor: ORANGE,
                    fontWeight: 700,
                    position: 'relative',
                    '&:hover': { bgcolor: ORANGE, filter: 'brightness(0.9)' },
                    '&:disabled': {
                      bgcolor: ORANGE,
                      color: '#fff',
                      opacity: 0.5,
                    },
                  }}
                >
                  {isLoading ? (
                    <>
                      <CircularProgress
                        size={20}
                        sx={{
                          position: 'absolute',
                          left: '50%',
                          marginLeft: '-10px',
                          color: '#fff',
                        }}
                      />
                      <span style={{ opacity: 0 }}>Get Started — $50</span>
                    </>
                  ) : (
                    'Get Started — $50'
                  )}
                </Button>

                {/* Loading Backdrop */}
                <Backdrop
                  sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    position: 'absolute',
                    borderRadius: 3,
                    bgcolor: 'rgba(10, 10, 10, 0.85)',
                  }}
                  open={isLoading}
                >
                  <Box textAlign="center">
                    <CircularProgress sx={{ color: ORANGE }} size={40} />
                    <Typography variant="h6" sx={{ mt: 2, color: TEXT }}>
                      Creating your storefront...
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, color: TEXT_DIM }}>
                      This may take a few moments
                    </Typography>
                  </Box>
                </Backdrop>

                <Typography
                  variant="caption"
                  sx={{ color: TEXT_DIM, textAlign: 'center', mt: 2, display: 'block' }}
                >
                  $50 one-time setup deposit — refunded on your first sale. No monthly fee. 5% success fee only on completed contracts.
                </Typography>
              </form>
            </CardContent>
          </Card>

          {/* Minimal footer links */}
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mt: 4 }}>
            {[
              { label: 'About Aithon', href: '/about' },
              { label: 'Privacy Policy', href: '/privacy' },
              { label: 'Terms of Service', href: '/terms' },
              { label: 'Contact', href: '/contact' },
            ].map((link) => (
              <Typography
                key={link.label}
                variant="body2"
                component="a"
                href={link.href}
                sx={{ color: TEXT_DIM, textDecoration: 'none', '&:hover': { color: TEXT } }}
              >
                {link.label}
              </Typography>
            ))}
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default RegisterPartner;

// Static pre-render so crawlers (GPTBot, ClaudeBot, Googlebot, etc.) see
// fully rendered HTML on the first HTTP response — no JS execution needed.
export const getStaticProps = async () => ({ props: {} });
